// src/oura/newscan/fixtures/brand.ts
//
// Brand mode (Visibility Set v4.5) in the fixture engine: how a brand draft's
// 50 are planned from a sample's pre-authored rows, the demand pull (Google
// suggestions per market) stored with the draft, and the words and places the
// brand lint bans. Spec: aeo_visibility_spec.json `modes.brand`, SPEC-v45.
//
// Shape (modes.brand.checks): pick 28 (pick_problem 20 + pick_product 8),
// source 14, supplier 8. A business that sells only to shoppers has 0 supplier
// rows and 28 pick_problem rows. Malay and Chinese rows, by share, replace the
// last English problem rows. Rows are ordered by group, then by the business's
// market priority (Any market last), so the home market's rows come last.
//
// The authored 50 fit the market list they were written for. Any other list
// (a market dropped or added, a new order) is drawn from the sample's pool of
// rows per market, so Draft again after a market change asks for the markets
// kept, the first gets the most and the home market the fewest.

import type {
    BrandQuestionCheck,
    ScanDraftBusiness,
    ScanDraftDemand,
    ScanDraftDemandSuggestion,
    ScanDraftMarket,
    ScanDraftQuestion,
    ScanMarketCode,
} from "@service/aeo/scanDraftTypes";
import { LANGUAGE_TIER_1, type TemplateDef } from "./recipe";
import type { PlannedRow } from "./render";
import type { BrandGroup, BrandPool, BrandRowDef, BrandSet, BrandSpare } from "./brandSamples";

export const BRAND_GROUPS: BrandGroup[] = ["pick_problem", "pick_product", "source", "supplier"];

export const CHECK_OF_GROUP: Record<BrandGroup, BrandQuestionCheck> = {
    pick_problem: "pick",
    pick_product: "pick",
    source: "source",
    supplier: "supplier",
};

/** modes.brand.checks: the counts a brand set must have. */
export const BRAND_SHAPE = { pick: 28, source: 14, supplier: 8, pick_product: 8, pick_problem: 20 } as const;

/** Every code a brand row's fill may hold. */
export const MARKET_CODES: ReadonlyArray<ScanMarketCode> = ["us", "my", "sg", "au", "uk", "global"];
/** The markets staff can list (Any market is never picked). */
export const PICKABLE_MARKETS: ReadonlyArray<ScanMarketCode> = ["us", "sg", "my", "au", "uk"];
export const MARKETS_MAX = 4;

/** As shown and as written in a question. */
export const MARKET_LABEL: Record<ScanMarketCode, string> = {
    us: "the US",
    my: "Malaysia",
    sg: "Singapore",
    au: "Australia",
    uk: "the UK",
    global: "Any market",
};

export function marketLabel(code: string): string {
    return MARKET_LABEL[code as ScanMarketCode] ?? code;
}

/** The code for a market label or code ("Malaysia", "my", "the US", "United States"). Null when unknown. */
export function marketCode(value: string): ScanMarketCode | null {
    const v = value.trim().toLowerCase().replace(/^the /, "");
    const hit = MARKET_CODES.find((c) => c === v || MARKET_LABEL[c].toLowerCase().replace(/^the /, "") === v);
    if (hit) return hit;
    const aliases: Record<string, ScanMarketCode> = {
        usa: "us",
        "united states": "us",
        "united kingdom": "uk",
        britain: "uk",
        malaysian: "my",
    };
    return aliases[v] ?? null;
}

export function marketOf(code: ScanMarketCode): ScanDraftMarket {
    return { code, label: MARKET_LABEL[code] };
}

/** The business's market codes in priority order, home last. */
export function marketCodes(b: ScanDraftBusiness): string[] {
    return (b.markets ?? []).map((m) => m.code);
}

/** "the US, Singapore and Malaysia". */
export function joinLabels(labels: string[]): string {
    if (labels.length <= 1) return labels[0] ?? "";
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/* ------------------------------------------------------------------------- *
 * Planning the 50
 * ------------------------------------------------------------------------- */

export interface BrandPlanInput {
    business: ScanDraftBusiness;
    set: BrandSet;
    languagesOn: { ms: boolean; zh: boolean };
    languageShare: { ms: number; zh: number };
    /** How many times the searches were pulled again: rows traced from a later pull have no trace before it. */
    demandRound: number;
}

export function brandKey(def: BrandRowDef): string {
    return `B:${def.id}`;
}

export function traceOf(def: BrandRowDef, round: number): { suggestion: string; market: string } | null {
    if ((def.tracedFromRound ?? 0) > round) return null;
    return { suggestion: def.suggestion, market: def.pulled };
}

function templateOf(def: BrandRowDef): TemplateDef {
    return { id: brandKey(def), text: def.firstDraft ?? def.question, check: CHECK_OF_GROUP[def.group], group: def.group };
}

function planned(def: BrandRowDef, round: number): PlannedRow {
    return {
        key: brandKey(def),
        check: CHECK_OF_GROUP[def.group],
        group: def.group,
        template: templateOf(def),
        ctx: {},
        town: null,
        place: def.market,
        language: def.language ?? "en",
        market: def.market,
        trace: traceOf(def, round),
        brand: def,
    };
}

/** How many Malay or Chinese rows a language gets: by share of the problem rows, at least 1 when on. */
function languageCount(on: boolean, share: number, problemRows: number, available: number): number {
    if (!on || !available) return 0;
    return Math.min(available, Math.max(1, Math.round(share * problemRows)));
}

/**
 * The authored 50 fit the markets they were written for, in that order. Any
 * other market list (one dropped, one added, a new order) is drawn from the
 * sample's pool, so the counts follow the list. A set with no pool always
 * uses its authored rows.
 */
export function usesPool(set: BrandSet, markets: string[]): boolean {
    return Boolean(set.pool) && markets.join(",") !== set.markets.join(",");
}

/** Every English row a pool draft can pick from: the authored rows, the stand-ins, then the pool. */
function poolRows(set: BrandSet): BrandRowDef[] {
    return [...set.rows, ...set.problemExtra, ...set.supplierExtra, ...(set.pool?.rows ?? [])];
}

/**
 * The market a row's search is filed under for this list: its own pull when
 * the list has that market, else the first listed market the pool was pulled
 * for (an Any market row is pulled with the first market).
 */
function pulledFor(set: BrandSet, markets: string[]): (d: BrandRowDef) => BrandRowDef {
    const all = poolRows(set);
    const first = markets.find((m) => all.some((d) => d.pulled === m)) ?? markets[0] ?? "global";
    return (d) => (markets.includes(d.pulled) ? d : { ...d, pulled: first });
}

/** Largest remainder: `total` split by `weights`, ties to the earlier market. */
function splitBy(total: number, weights: number[]): number[] {
    const sum = weights.reduce((a, b) => a + b, 0);
    if (!sum || total <= 0) return weights.map(() => 0);
    const exact = weights.map((w) => (total * w) / sum);
    const out = exact.map(Math.floor);
    let left = total - out.reduce((a, b) => a + b, 0);
    const order = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
    for (const { i } of order) {
        if (left <= 0) break;
        out[i] += 1;
        left -= 1;
    }
    return out;
}

/**
 * The 50 drawn from the pool for the business's market list (modes.brand):
 * per group, the Any market rows first take their share, the rest is split
 * across the listed markets by priority (the first gets the most, the home
 * market last with the fewest). A market the pool has too few rows for passes
 * its share on: to the other non-home markets, then to Any market, then to
 * the home market. A listed market the pool has no rows for gets none.
 */
function poolDefs(set: BrandSet, markets: string[], sells: boolean): BrandRowDef[] {
    const pool = set.pool as BrandPool;
    const all = poolRows(set);
    const shape: Record<BrandGroup, number> = sells
        ? { pick_problem: BRAND_SHAPE.pick_problem, pick_product: BRAND_SHAPE.pick_product, source: BRAND_SHAPE.source, supplier: BRAND_SHAPE.supplier }
        : { pick_problem: BRAND_SHAPE.pick_problem + BRAND_SHAPE.supplier, pick_product: BRAND_SHAPE.pick_product, source: BRAND_SHAPE.source, supplier: 0 };
    const anyShare = pool.global[sells ? "sells" : "shoppers"];
    const home = markets[markets.length - 1];
    const named = markets.filter((m) => all.some((d) => d.market === m));
    const others = named.filter((m) => m !== home);
    // Home 1; the other markets 3 per step above it (two markets 3:1, three 6:3:1).
    const weights = named.map((m) => (m === home ? 1 : 3 * (others.length - others.indexOf(m))));
    const out: BrandRowDef[] = [];
    BRAND_GROUPS.forEach((g) => {
        const want = shape[g];
        const rowsIn = (m: string) => all.filter((d) => d.group === g && d.market === m);
        const anyRows = rowsIn("global");
        let anyTake = Math.min(anyShare[g], want);
        const take = new Map<string, number>();
        let spill = 0;
        splitBy(want - anyTake, weights).forEach((n, i) => {
            const got = Math.min(n, rowsIn(named[i]).length);
            take.set(named[i], got);
            spill += n - got;
        });
        if (!named.length) spill = want - anyTake;
        const room = (m: string) => rowsIn(m).length - (take.get(m) ?? 0);
        const pass = (m: string) => {
            const n = Math.min(room(m), spill);
            take.set(m, (take.get(m) ?? 0) + n);
            spill -= n;
        };
        others.forEach(pass);
        const toAny = Math.min(anyRows.length - anyTake, spill);
        anyTake += toAny;
        spill -= toAny;
        if (named.includes(home)) pass(home);
        named.forEach((m) => out.push(...rowsIn(m).slice(0, take.get(m) ?? 0)));
        out.push(...anyRows.slice(0, anyTake));
    });
    return out.map(pulledFor(set, markets));
}

/** The authored 50 for who the business sells to: supplier rows only when it sells to businesses. */
function authoredDefs(set: BrandSet, sells: boolean): BrandRowDef[] {
    const defs = [...set.rows];
    const hasSupplier = defs.some((d) => d.group === "supplier");
    if (!sells && hasSupplier) {
        // Shoppers only: the 8 supplier rows go to buyer problems.
        return defs.filter((d) => d.group !== "supplier").concat(set.problemExtra);
    }
    if (sells && !hasSupplier && set.supplierExtra.length) {
        // Also sells to businesses: the last 8 problem rows make way for 8 supplier rows.
        const problems = defs.filter((d) => d.group === "pick_problem");
        const drop = new Set(problems.slice(-set.supplierExtra.length).map((d) => d.id));
        return defs.filter((d) => !drop.has(d.id)).concat(set.supplierExtra);
    }
    return defs;
}

/** The row definitions the business's 50 are made of, in group and market order. */
export function brandDefs(input: BrandPlanInput): BrandRowDef[] {
    const { set, business } = input;
    const sells = business.sellsToBusinesses === true;
    const priority = marketCodes(business);
    const pooled = usesPool(set, priority);
    // A pool draft is drawn in the shape for who it sells to; the authored 50 are adjusted to it.
    let defs = pooled ? poolDefs(set, priority, sells) : authoredDefs(set, sells);

    // Malay and Chinese rows replace the last English problem rows (never a planted one: its fix is the walk-through).
    // A pool draft only takes the ones asked for a market on its list.
    const languageRows = pooled
        ? set.languageRows.filter((r) => r.market === "global" || priority.includes(r.market)).map(pulledFor(set, priority))
        : set.languageRows;
    const problemCount = defs.filter((d) => d.group === "pick_problem").length;
    const take = (lang: "ms" | "zh", on: boolean, share: number) => {
        const rows = languageRows.filter((r) => r.language === lang);
        return rows.slice(0, languageCount(on, share, problemCount, rows.length));
    };
    const langRows = [...take("ms", input.languagesOn.ms, input.languageShare.ms), ...take("zh", input.languagesOn.zh, input.languageShare.zh)];
    if (langRows.length) {
        const replaceable = defs.filter((d) => d.group === "pick_problem" && (d.language ?? "en") === "en" && !d.firstDraft);
        const out = replaceable.slice(-langRows.length);
        const swap = new Map(out.map((d, i) => [d.id, langRows[i]]));
        defs = defs.map((d) => swap.get(d.id) ?? d);
    }

    // Group order. The authored order already follows the markets it was written for; when the
    // business puts its markets in another order, rows follow that priority (Any market last).
    const reordered = priority.join(",") !== set.markets.join(",");
    const rank = (m: string) => (!reordered ? 0 : m === "global" ? 100 : priority.includes(m) ? priority.indexOf(m) : 50);
    return defs
        .map((d, i) => ({ d, i }))
        .sort((a, b) => BRAND_GROUPS.indexOf(a.d.group) - BRAND_GROUPS.indexOf(b.d.group) || rank(a.d.market) - rank(b.d.market) || a.i - b.i)
        .map(({ d }) => d);
}

export function brandPlan(input: BrandPlanInput): PlannedRow[] {
    return brandDefs(input).map((d) => planned(d, input.demandRound));
}

/** Language rows on by the business's choice, or Auto from the tier-1 share. */
export function brandLanguagesOn(b: ScanDraftBusiness, share: { ms: number; zh: number }): { ms: boolean; zh: boolean } {
    const on = (mode: string, s: number) => mode === "on" || (mode === "auto" && s >= LANGUAGE_TIER_1);
    return { ms: on(b.languages.ms, share.ms), zh: on(b.languages.zh, share.zh) };
}

/* ------------------------------------------------------------------------- *
 * Try another: other pulled searches, same group and market
 * ------------------------------------------------------------------------- */

export const spareId = (s: BrandSpare) => `d:${s.market}:${s.text}`;

export function spareById(set: BrandSet | null | undefined, id: string): BrandSpare | null {
    if (!set) return null;
    return set.spares.find((s) => spareId(s) === id) ?? null;
}

/** The spare searches a row could be asked as instead: same group, pulled for the row's market (or the first pull for Any market). */
export function sparesFor(set: BrandSet, def: BrandRowDef): BrandSpare[] {
    return set.spares.filter((s) => s.question && s.group === def.group && s.market === def.pulled);
}

/* ------------------------------------------------------------------------- *
 * The demand pull
 * ------------------------------------------------------------------------- */

interface PulledItem {
    text: string;
    market: string;
    seed: string;
    recurs: number;
}

/**
 * Every suggestion the pull returned, deduped per market: the ones the
 * authored rows and their stand-ins were written from, the spares, then
 * generated filler up to the market's count. Deterministic.
 */
function pulledItems(set: BrandSet, round: number, markets: string[]): PulledItem[] {
    // A pool draft pulled only its listed markets: the pool's searches for them, nothing else.
    const pooled = usesPool(set, markets);
    const out: PulledItem[] = [];
    const seen = new Set<string>();
    const add = (item: PulledItem) => {
        const k = `${item.market}|${item.text.toLowerCase()}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push(item);
    };
    const defs = pooled
        ? [...poolRows(set), ...set.languageRows].map(pulledFor(set, markets)).filter((d) => markets.includes(d.pulled))
        : [...set.rows, ...set.problemExtra, ...set.supplierExtra, ...set.languageRows];
    defs.forEach((d, i) => {
        if ((d.tracedFromRound ?? 0) > round) return;
        add({ text: d.suggestion, market: d.pulled, seed: d.seed, recurs: Math.max(2, 9 - Math.floor(i / 7)) });
    });
    set.spares.forEach((s) => add({ text: s.text, market: s.market, seed: s.seed, recurs: s.question ? 2 : 1 }));
    Object.entries(set.filler).forEach(([market, f]) => {
        if (pooled && !markets.includes(market)) return;
        const target = set.pulledPerMarket[market] ?? 0;
        let count = out.filter((x) => x.market === market).length;
        for (const ending of f.endings) {
            for (const seed of f.seeds) {
                if (count >= target) return;
                const before = out.length;
                add({ text: `${seed} ${ending}`, market, seed, recurs: 1 });
                if (out.length > before) count += 1;
            }
        }
    });
    const order = (m: string) => (markets.includes(m) ? markets.indexOf(m) : 50);
    return out
        .filter((x) => markets.includes(x.market) || (!pooled && defs.some((d) => d.pulled === x.market)))
        .sort((a, b) => order(a.market) - order(b.market) || b.recurs - a.recurs || a.text.localeCompare(b.text));
}

/** The pull as stored with the draft: summary, untraced rows and the ranked list with what uses each search. */
export function brandDemand(set: BrandSet, business: ScanDraftBusiness, questions: ScanDraftQuestion[], round: number, pulledAt: number): ScanDraftDemand {
    const markets = marketCodes(business);
    const items: ScanDraftDemandSuggestion[] = pulledItems(set, round, markets).map((x) => ({
        text: x.text,
        market: x.market,
        seed: x.seed,
        recurs: x.recurs,
        usedBy: questions
            .filter((q) => q.trace && q.trace.market === x.market && q.trace.suggestion.toLowerCase() === x.text.toLowerCase())
            .map((q) => q.n),
    }));
    return {
        pulledAt: new Date(pulledAt).toISOString(),
        markets,
        suggestions: items.length,
        untraced: questions.filter((q) => q.trace === null).map((q) => q.n),
        items,
    };
}

/** Below this many suggestions a market's pull is thin (the `demand_thin` note). */
export const DEMAND_THIN_BELOW = 30;

/* ------------------------------------------------------------------------- *
 * What a brand question may not say
 * ------------------------------------------------------------------------- */

/** Towns and neighbourhoods a brand question must not name (a market word is fine; Singapore is a market). */
const CITIES = [
    "kuala lumpur", "kl", "petaling jaya", "pj", "bangsar", "mont kiara", "subang jaya", "subang", "damansara", "puchong", "cheras",
    "penang", "george town", "johor bahru", "jb", "johor", "klang", "shah alam", "ipoh", "melaka", "cyberjaya", "putrajaya", "kota kinabalu",
    "kuching", "orchard", "jurong", "tampines", "woodlands", "new york", "nyc", "los angeles", "san francisco", "chicago", "houston", "miami",
    "london", "manchester", "birmingham", "sydney", "melbourne", "brisbane", "perth",
];

/** Buildings, malls and the words that name one. */
const BUILDINGS = [
    "menara", "plaza", "mall", "mid valley", "pavilion", "sunway pyramid", "1 utama", "ioi city", "suria klcc", "klcc", "jaya one", "publika",
    "the gardens", "bangsar village", "marina bay sands", "ion orchard",
];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Whole words for Latin text; the phrases are plain letters, digits, spaces, dots and hyphens. */
const wordRe = (w: string) => new RegExp(`(^|[^a-z0-9])${escapeRe(w)}($|[^a-z0-9])`, "i");
/** CJK has no word breaks: a plain substring. */
const hasCjk = (s: string) => /[\u3040-\u30ff\u3400-\u9fff]/.test(s);

/** Whether `text` holds `phrase` as whole words (a substring for Chinese). */
export function containsPhrase(text: string, phrase: string): boolean {
    const p = phrase.trim();
    if (!p) return false;
    return hasCjk(p) ? text.includes(p) : wordRe(p).test(text);
}

export type PlaceHit = { kind: "city" | "building" | "near_me"; word: string };

/** The first city, building or "near me" a brand question names. `extra` are the business's own areas and buildings. */
export function placeIn(text: string, extra: string[] = []): PlaceHit | null {
    if (/\bnear me\b/i.test(text)) return { kind: "near_me", word: "near me" };
    // The word as the question writes it ("KL", not "kl").
    const asWritten = (w: string) => {
        const i = text.toLowerCase().indexOf(w.toLowerCase());
        return i >= 0 ? text.slice(i, i + w.length) : w;
    };
    const building = BUILDINGS.find((w) => containsPhrase(text, w));
    if (building) return { kind: "building", word: asWritten(building) };
    const city = [...CITIES, ...extra.map((x) => x.toLowerCase())].find((w) => w && containsPhrase(text, w));
    return city ? { kind: "city", word: asWritten(city) } : null;
}

/** "Best supplement for joint pain in KL" -> "Best supplement for joint pain in Malaysia" (or without the place for Any market). */
export function withoutPlace(text: string, hit: PlaceHit, market: string): string {
    const label = market && market !== "global" ? marketLabel(market) : "";
    const re = new RegExp(`\\s*\\b(in|at|near)\\s+${escapeRe(hit.word)}\\b`, "i");
    let out = hit.kind === "near_me" ? text.replace(/\s*\bnear me\b/i, "") : re.test(text) ? text.replace(re, label ? ` in ${label}` : "") : text;
    if (out === text) out = text.replace(new RegExp(`\\s*\\b${escapeRe(hit.word)}\\b`, "i"), label ? ` in ${label}` : "");
    return out.replace(/\s+/g, " ").trim();
}

/** Sentence case: "best supplement for stiff knees" -> "Best supplement for stiff knees". */
export function sentenceCase(s: string): string {
    const t = s.trim();
    return t ? t[0].toUpperCase() + t.slice(1) : t;
}

/** Words in a value. */
export const wordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
export const DIFFERENTIATOR_MAX_WORDS = 5;

export type { BrandGroup, BrandRowDef, BrandSet, BrandSpare };
